import * as React from 'react';
import { IconSizes, type NewcoreIcon } from '@newcore/icons';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { Sizes } from '../../enums/sizes';
import { ButtonVariants } from '../../enums/variants';
import { cn } from '../../utils/cn';
import { buttonVariants } from '../../variants/button-variants';
import { Spinner } from '../loading';
import { Tooltip } from '../tooltips';

export type ButtonSize =
  | Sizes.XS
  | Sizes.SM
  | Sizes.MD
  | Sizes.LG
  | Sizes.XL
  | Sizes.FILTER
  | Sizes.ICON;

type ButtonElementProps = Omit<
  React.ComponentProps<'button'>,
  'disabled' | 'asChild'
>;

interface SharedButtonProps extends ButtonElementProps {
  size?: ButtonSize;
  iconLeading?: NewcoreIcon;
  iconTrailing?: NewcoreIcon;
}

type ButtonVariantProps =
  | {
      variant?: Exclude<ButtonVariants, ButtonVariants.ICON>;
      tooltip?: string;
    }
  | {
      variant: ButtonVariants.ICON;
      tooltip?: string;
    };

export type ButtonProps = ButtonVariantProps &
  (
    | (SharedButtonProps & {
        asChild?: false;
        isLoading?: boolean;
        disabled?: boolean;
      })
    | (SharedButtonProps & {
        asChild: true;
        isLoading?: never;
        disabled?: never;
      })
  );

function Button({
  className,
  variant = ButtonVariants.PRIMARY,
  size = Sizes.SM,
  asChild = false,
  isLoading = false,
  disabled,
  iconLeading,
  iconTrailing,
  tooltip,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const IconLeading = iconLeading;
  const IconTrailing = iconTrailing;
  const isIconOnly = variant === ButtonVariants.ICON;
  const isDisabled = disabled || isLoading;

  const button = (
    <Comp
      data-slot='button'
      data-variant={variant}
      data-size={size}
      disabled={isDisabled}
      className={cn(
        buttonVariants({ variant, size }),
        isLoading && 'relative',
        className
      )}
      {...props}
      aria-label={
        isIconOnly ? (tooltip ?? props['aria-label']) : props['aria-label']
      }
      aria-busy={isLoading || undefined}
    >
      {isLoading && (
        <span
          aria-hidden
          className='absolute inset-0 flex items-center justify-center'
        >
          <Spinner size={IconSizes.SM} />
        </span>
      )}
      {[
        IconLeading && (
          <IconLeading
            key='leading'
            aria-hidden={isLoading || undefined}
            className={isLoading ? 'opacity-0' : undefined}
          />
        ),
        <Slottable key='content'>
          {!asChild && children !== null && children !== undefined ? (
            <span
              className={cn(
                typeof children === 'string' || typeof children === 'number'
                  ? 'min-w-0 truncate'
                  : 'inline-flex items-center gap-1.5',
                !iconLeading && 'pl-0.5',
                !iconTrailing && 'pr-0.5',
                isLoading && 'opacity-0'
              )}
            >
              {children}
            </span>
          ) : (
            children
          )}
        </Slottable>,
        IconTrailing && (
          <IconTrailing
            key='trailing'
            aria-hidden={isLoading || undefined}
            className={isLoading ? 'opacity-0' : undefined}
          />
        ),
      ]}
    </Comp>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip}>
        {isDisabled ? <span className='inline-flex'>{button}</span> : button}
      </Tooltip>
    );
  }

  return button;
}

export { Button };
